import { instance } from "../axios/instance";
import { PATH } from "../shared/constants";
import { StorageServiceInstance } from "../services/storage";
import { Routes } from "./routes";

/**
 * Page-view funnel steps. Each SPA navigation that maps here increments
 * visitCount for (team, sessionId, step, gameKey) so unique sessions and
 * total page views stay accurate without one row per hit.
 */
export type FanFunnelStep =
  | "welcome"
  | "games"
  | "details"
  | "seat"
  | "ga_where"
  | "ga_area"
  | "ga_map"
  | "place"
  | "my_moments"
  | "download"
  | "purchase"
  /** Button clicks (not page views) — counted under Funnel → Button clicks. */
  | "btn_previous_session"
  | "btn_view_all"
  | "btn_edit_crop";

const SESSION_STORAGE_KEY = "momento_fan_funnel_session_id";
const ACQUISITION_STORAGE_KEY = "momento_fan_acquisition_touch";

const PATH_TO_FUNNEL_STEP: Record<string, FanFunnelStep> = {
  [Routes.WELCOME]: "welcome",
  [Routes.GAMES]: "games",
  [Routes.DETAILS]: "details",
  [Routes.SEAT]: "seat",
  [Routes.VERIFY_SEAT]: "seat",
  [Routes.PLACE]: "place",
  [Routes.ENHANCED_GA_WHERE]: "ga_where",
  [Routes.ENHANCED_GA_AREA]: "ga_area",
  [Routes.ENHANCED_GA_MAP]: "ga_map",
  [Routes.MY_MOMENTS]: "my_moments",
};

export interface FanAcquisitionTouch {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  referrer?: string;
}

function createSessionId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `ff_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

export function getFanFunnelSessionId(): string {
  if (typeof window === "undefined") return createSessionId();
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing && existing.length >= 8) return existing;
    const next = createSessionId();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, next);
    return next;
  } catch {
    return createSessionId();
  }
}

/** Subdomain used as the team's classified fan hostname (e.g. "mariners"). */
export function getFanHostnamePrefix(): string | null {
  if (typeof window === "undefined") return null;
  const { hostname } = window.location;
  if (!hostname) return null;
  if (hostname === "localhost" || hostname === "127.0.0.1") return null;
  const prefix = hostname.split(".")[0]?.toLowerCase()?.trim();
  if (!prefix || prefix === "www") return null;
  return prefix;
}

function resolveTeamId(): string | null {
  const fromGame = StorageServiceInstance.selectedGameData?.team1?._id;
  if (fromGame) return String(fromGame);
  const fromWelcome = StorageServiceInstance.welcomeTeamContext?._id;
  if (fromWelcome) return String(fromWelcome);
  return null;
}

function resolveGameId(): string | null {
  const id = StorageServiceInstance.selectedGameData?._id
    || StorageServiceInstance.selectedGame;
  return id ? String(id) : null;
}

function resolveEmail(): string | null {
  const email = StorageServiceInstance.selectedEmail;
  return email ? String(email).trim() : null;
}

function readUtmFromSearch(search: string): Omit<FanAcquisitionTouch, "referrer"> {
  try {
    const params = new URLSearchParams(search || "");
    const pick = (key: string) => {
      const v = params.get(key);
      return v ? v.trim().slice(0, 200) : undefined;
    };
    return {
      utmSource: pick("utm_source"),
      utmMedium: pick("utm_medium"),
      utmCampaign: pick("utm_campaign"),
      utmContent: pick("utm_content"),
      utmTerm: pick("utm_term"),
    };
  } catch {
    return {};
  }
}

/**
 * First-touch acquisition for this browser session.
 * Locked on first read so later in-flow navigations do not overwrite source.
 */
export function getFanAcquisitionTouch(): FanAcquisitionTouch {
  if (typeof window === "undefined") return {};
  try {
    const existing = window.sessionStorage.getItem(ACQUISITION_STORAGE_KEY);
    if (existing) {
      return JSON.parse(existing) as FanAcquisitionTouch;
    }
  } catch {
    // ignore
  }

  const utms = readUtmFromSearch(window.location.search || "");
  let referrer = "";
  try {
    referrer = String(document.referrer || "").trim().slice(0, 500);
    // Ignore same-host navigations (SPA / internal) as acquisition referrers.
    if (referrer) {
      const refHost = new URL(referrer).hostname;
      if (refHost === window.location.hostname) referrer = "";
    }
  } catch {
    referrer = "";
  }

  const touch: FanAcquisitionTouch = {
    ...utms,
    ...(referrer ? { referrer } : {}),
  };

  try {
    window.sessionStorage.setItem(ACQUISITION_STORAGE_KEY, JSON.stringify(touch));
  } catch {
    // ignore
  }
  return touch;
}

/** Map a fan SPA path to a funnel step, or null if the path is not part of the funnel. */
export function getFunnelStepFromPath(pathname: string): FanFunnelStep | null {
  return PATH_TO_FUNNEL_STEP[pathname] || null;
}

/**
 * Fire-and-forget funnel step + first-touch acquisition to the API.
 * Welcome landings can send hostname alone (mariners.mymomento.com → "mariners")
 * so they count before team context hydrates — visits do not require an email.
 */
export function trackFanFunnelStep(step: FanFunnelStep): void {
  const teamId = resolveTeamId();
  const hostname = getFanHostnamePrefix();
  if (!teamId && !hostname) return;

  const payload = {
    teamId: teamId || undefined,
    hostname: hostname || undefined,
    sessionId: getFanFunnelSessionId(),
    step,
    gameId: resolveGameId(),
    email: resolveEmail(),
    acquisition: getFanAcquisitionTouch(),
  };

  instance
    .post(`${PATH.API}/fan-funnel/event`, payload)
    .catch((err) => {
      // Surface failures in the console — silent drop made funnel look permanently broken.
      console.warn("[fan-funnel] event failed", step, err?.response?.status, err?.response?.data || err);
    });
}

export function trackFanFunnelPageView(pathname: string): void {
  const step = getFunnelStepFromPath(pathname);
  if (!step) return;
  trackFanFunnelStep(step);
}

/** Track a UI button press in the Engagement Funnel Button clicks section. */
export function trackFanButtonClick(
  button: "btn_previous_session" | "btn_view_all" | "btn_edit_crop",
): void {
  trackFanFunnelStep(button);
}
